import { useCallback } from "react";
import { useSelector } from "react-redux";
// @ts-expect-error: ignoring as it is existing js file
import { userDetailsSelector } from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import { getHasInvalidError, getInvalidErrorValue, invalidKey } from "./utils/conditionalFields";

interface SelectOption {
  label: string;
  value: string;
}

const getRelatedAddressFieldError = ({
  errors,
  fieldName,
}: {
  errors: Record<string, string>;
  fieldName: string;
}) => {
  const [rawFieldName, ...identifierParts] = fieldName.split("_");
  const identifier = identifierParts.join("_");

  if (!rawFieldName.includes(".address") || !identifier) {
    return undefined;
  }

  return Object.entries(errors ?? {}).find(([key]) => {
    return key.startsWith(`${rawFieldName}.`) && key.endsWith(`_${identifier}`);
  })?.[1];
};

const useGetLocalAndLenderErrors = () => {
  const { validationRequireFieldError } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const { customerErrors, assetErrors, agreementError, entityFieldErrors, loanDetailsErrors } =
    validationRequireFieldError;

  const getAssetOrLoanDetailsLocalAndLenderError = useCallback(
    ({
      fieldError,
      localState,
      fieldName,
      localFieldName,
      isLoanDetails = false,
    }: {
      fieldError?: string;
      localState: Record<string, string | SelectOption>;
      fieldName: string;
      localFieldName?: string;
      isLoanDetails?: boolean;
    }) => {
      const errors = isLoanDetails ? loanDetailsErrors : assetErrors;
      const hasInvalidError = getHasInvalidError({ errors, fieldName });
      if (hasInvalidError) {
        return errors?.[`${fieldName}_${invalidKey}`];
      }
      if (fieldError) {
        return fieldError;
      }
      if (typeof localState[localFieldName ?? fieldName] === "string" && !!localState[localFieldName ?? fieldName]) {
        return undefined;
      }
      if (
        typeof localState[localFieldName ?? fieldName] === "object" &&
        !!(localState[localFieldName ?? fieldName] as SelectOption)?.label
      ) {
        return undefined;
      }
      const lenderError = errors?.[fieldName];
      return lenderError;
    },
    [assetErrors, loanDetailsErrors],
  );

  const getCustomerOrEntityLocalAndLenderError = useCallback(
    ({
      fieldError,
      localState,
      fieldName,
      localFieldName,
      doNotShowError = false,
      isEntity = false,
    }: {
      fieldError?: string;
      localState: Record<string, string>;
      fieldName: string;
      localFieldName?: string;
      doNotShowError?: boolean;
      isEntity?: boolean;
    }) => {
      const errors = isEntity ? entityFieldErrors : customerErrors;
      const invalidErrorValue = getInvalidErrorValue({ errors, fieldName });
      if (invalidErrorValue) {
        return invalidErrorValue;
      }
      const relatedAddressFieldError = getRelatedAddressFieldError({ errors, fieldName });
      if (relatedAddressFieldError) {
        return relatedAddressFieldError;
      }
      if (doNotShowError) {
        return undefined;
      }
      const splitFieldName = fieldName.split("_")[0];
      const lenderError = errors?.[fieldName];
      return fieldError || (localState[localFieldName ?? splitFieldName] ? false : lenderError);
    },
    [customerErrors, entityFieldErrors],
  );

  const getAgreementLocalAndLenderError = useCallback(
    ({
      fieldError,
      localState,
      fieldName,
      localFieldName,
    }: {
      fieldError?: string;
      localState: Record<string, string>;
      fieldName: string;
      localFieldName?: string;
    }) => {
      return fieldError || (localState[localFieldName ?? fieldName] ? false : agreementError?.[fieldName]);
    },
    [agreementError],
  );

  return {
    getCustomerOrEntityLocalAndLenderError,
    getAssetOrLoanDetailsLocalAndLenderError,
    getAgreementLocalAndLenderError,
  };
};

export { useGetLocalAndLenderErrors };
